/**
 * Centralized Environment and Hostname Parsing Utility
 *
 * This service provides a single source of truth for:
 * - Environment detection (dev/qa/production)
 * - Community/organization extraction from hostname
 * - Localhost and local development subdomain handling (local.app.{env})
 * - URL building for cross-subdomain navigation
 */

export type Environment = "dev" | "qa" | "pt" | "production" | "localhost";

export interface EnvironmentInfo {
    /** Current environment: dev, qa, production, or localhost */
    env: Environment;
    /** Community/organization name extracted from hostname (e.g., 'aqsacom' from app.dev.aqsacom.corridorsafety.com) */
    community: string | null;
    /** True if running on localhost or 127.0.0.1 */
    isLocalhost: boolean;
    /** True if running on local.app.{env} pattern for local development with subdomains */
    isLocalDev: boolean;
    /** True if running in production environment */
    isProduction: boolean;
}

export class EnvironmentService {
    private static readonly DOMAIN = "corridorsafety.com";
    private static readonly SERVICE_SUBDOMAINS = ["app", "consumer", "local"];
    private static readonly POSSIBLE_ENVS = ["dev", "qa", "pt", "localhost"];

    private static cachedInfo: EnvironmentInfo | null = null;

    /**
     * Parse the current hostname and extract environment information
     */
    static parseEnvironment(): EnvironmentInfo {
        // Return cached result if available (hostname doesn't change during session)
        if (this.cachedInfo) {
            return this.cachedInfo;
        }

        const hostname = window.location.hostname;

        // Handle localhost for traditional local development
        if (hostname === "localhost" || hostname === "127.0.0.1") {
            this.cachedInfo = {
                env: "localhost",
                community: this.getLocalhostCommunityFromParams(),
                isLocalhost: true,
                isLocalDev: false,
                isProduction: false,
            };
            return this.cachedInfo;
        }

        // Handle local.app.{env} pattern for local development with subdomains
        if (hostname.startsWith("local.app.")) {
            const { community, env } =
                this.extractCommunityAndEnvFromLocalDev(hostname);
            this.cachedInfo = {
                env,
                community,
                isLocalhost: false,
                isLocalDev: true,
                isProduction: false,
            };
            return this.cachedInfo;
        }

        // Handle corridorsafety.com domains
        if (!hostname.endsWith(this.DOMAIN)) {
            console.warn(
                `Unrecognized hostname: ${hostname}. Using default configuration.`,
            );
            this.cachedInfo = {
                env: "dev",
                community: null,
                isLocalhost: false,
                isLocalDev: false,
                isProduction: false,
            };
            return this.cachedInfo;
        }

        // Handle the production root domain
        if (hostname === this.DOMAIN) {
            this.cachedInfo = {
                env: "production",
                community: null,
                isLocalhost: false,
                isLocalDev: false,
                isProduction: true,
            };
            return this.cachedInfo;
        }

        // Parse subdomains from corridorsafety.com hostname
        const subdomains = hostname
            .substring(0, hostname.length - this.DOMAIN.length - 1)
            .split(".");

        let env: Environment = "production";

        // Find environment subdomain
        const envIndex = subdomains.findIndex((s) =>
            this.POSSIBLE_ENVS.includes(s),
        );
        if (envIndex > -1) {
            env = subdomains[envIndex] as Environment;
            subdomains.splice(envIndex, 1);
        }

        // Filter out service subdomains to get community name
        const communitySubdomains = subdomains.filter(
            (s) => !this.SERVICE_SUBDOMAINS.includes(s),
        );

        const community =
            communitySubdomains.length > 0
                ? communitySubdomains.join(".")
                : null;

        this.cachedInfo = {
            env,
            community,
            isLocalhost: false,
            isLocalDev: false,
            isProduction: env === "production",
        };

        return this.cachedInfo;
    }

    /**
     * Extract community and environment from local.app.{env}.{community}.corridorsafety.com pattern
     * Examples:
     * - local.app.dev.aqsacom.corridorsafety.com -> { community: 'aqsacom', env: 'dev' }
     * - local.app.qa.dubuquehs.corridorsafety.com -> { community: 'dubuquehs', env: 'qa' }
     * - local.app.dubuquehs.corridorsafety.com -> { community: 'dubuquehs', env: 'production' }
     * - local.app.qa.corridorsafety.com -> { community: null, env: 'qa' }
     * - local.app.corridorsafety.com -> { community: null, env: 'production' }
     */
    private static extractCommunityAndEnvFromLocalDev(hostname: string): {
        community: string | null;
        env: Environment;
    } {
        // local.app.dev.aqsacom.corridorsafety.com -> ['local', 'app', 'dev', 'aqsacom', 'corridorsafety', 'com']
        const parts = hostname.split(".");

        // Validate minimum structure: local.app.corridorsafety.com (4 parts)
        if (parts.length < 4) {
            return { community: null, env: "production" };
        }

        // Find the environment subdomain (dev or qa)
        const envIndex = parts.findIndex((part) =>
            this.POSSIBLE_ENVS.includes(part),
        );

        if (envIndex === -1 || envIndex > parts.length - 3) {
            // No environment found or it's after the domain position - default to production
            // This handles: local.app.corridorsafety.com or local.app.aqsacom.corridorsafety.com
            const communityParts = parts.slice(2, parts.length - 2); // Everything between 'app' and the domain
            const community =
                communityParts.length > 0 ? communityParts.join(".") : null;
            return { community, env: "production" };
        }

        const env = parts[envIndex] as Environment;

        // Extract everything between the environment and the domain (last 2 parts)
        const communityParts = parts.slice(envIndex + 1, parts.length - 2);
        const community =
            communityParts.length > 0 ? communityParts.join(".") : null;

        return { community, env };
    }

    /**
     * Get community from URL parameters when on localhost (for testing)
     */
    private static getLocalhostCommunityFromParams(): string | null {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get("org") || urlParams.get("community") || null;
    }

    /**
     * Clear the cached environment info (useful for testing)
     */
    static clearCache(): void {
        this.cachedInfo = null;
    }

    /**
     * Get current environment name
     */
    static getEnvironment(): Environment {
        return this.parseEnvironment().env;
    }

    /**
     * Get current community/organization
     */
    static getCommunity(): string | null {
        return this.parseEnvironment().community;
    }

    /**
     * Check if currently on a community-specific domain
     * (e.g., app.dev.aqsacom.corridorsafety.com vs app.dev.corridorsafety.com)
     */
    static isCommunityDomain(): boolean {
        return this.getCommunity() !== null;
    }

    /**
     * Check if running on localhost
     */
    static isLocalhost(): boolean {
        return this.parseEnvironment().isLocalhost;
    }

    /**
     * Check if running on local dev subdomain pattern
     */
    static isLocalDev(): boolean {
        return this.parseEnvironment().isLocalDev;
    }

    /**
     * Check if running in production
     */
    static isProduction(): boolean {
        return this.parseEnvironment().isProduction;
    }

    /**
     * Check if running in development
     */
    static isDev(): boolean {
        return this.parseEnvironment().env === "dev";
    }

    /**
     * Check if running in QA
     */
    static isQA(): boolean {
        return this.parseEnvironment().env === "qa";
    }

    /**
     * Check if running in PT
     */
    static isPT(): boolean {
        return this.parseEnvironment().env === "pt";
    }

    /**
     * Get environment prefix for URL building (e.g., 'dev.' or '')
     */
    static getEnvironmentPrefix(): string {
        const { env } = this.parseEnvironment();
        return env === "production" ? "" : `${env}.`;
    }

    /**
     * Get local prefix for URL building (e.g., 'local.' or '')
     */
    static getLocalPrefix(): string {
        return this.isLocalDev() ? "local." : "";
    }

    /**
     * Get the appropriate protocol based on environment
     */
    static getProtocol(): string {
        const { isLocalhost, isLocalDev } = this.parseEnvironment();
        return isLocalhost || isLocalDev ? window.location.protocol : "https:";
    }

    /**
     * Get port string for URL building (includes colon)
     */
    static getPort(): string {
        const { isLocalhost, isLocalDev } = this.parseEnvironment();
        if ((isLocalhost || isLocalDev) && window.location.port) {
            return `:${window.location.port}`;
        }
        return "";
    }

    /**
     * Build a complete URL (private helper for both community and top-level URLs)
     */
    private static buildUrl(community?: string): string {
        const { isLocalhost } = this.parseEnvironment();

        // For traditional localhost development, stay on localhost
        if (isLocalhost) {
            return `${window.location.protocol}//${window.location.hostname}${this.getPort()}`;
        }

        // Build the URL using the same pattern for both local and hosted environments
        const protocol = this.getProtocol();
        const localPrefix = this.getLocalPrefix();
        const envPrefix = this.getEnvironmentPrefix();
        const port = this.getPort();
        const communityPart = community ? `${community}.` : "";

        return `${protocol}//${localPrefix}app.${envPrefix}${communityPart}${this.DOMAIN}${port}`;
    }

    /**
     * Build a complete URL for a specific community subdomain
     */
    static buildCommunityUrl(community: string): string {
        return this.buildUrl(community);
    }

    /**
     * Build URL for the top-level app domain (without community)
     */
    static buildTopLevelAppUrl(): string {
        return this.buildUrl();
    }

    /**
     * Check if a given URL would be a different origin from current location
     */
    static isDifferentOrigin(url: string): boolean {
        const { isLocalhost } = this.parseEnvironment();

        // Never consider different origin when on traditional localhost
        if (isLocalhost) return false;

        return window.location.origin !== new URL(url).origin;
    }
}
